import { orgSelectors } from 'ducks/orgs'
import AvailableStocksButton from 'elements/hardwareSelectorV2/elements/AvailableStocksButton'
import {
  getStockStatusMessagesPerDistributor,
  STOCK_LEVEL_SCENARIO,
} from 'elements/hardwareSelectorV2/elements/stockUtils'
import { DistributorTradePricingContext } from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import type { DistributorDataTypeV2 } from 'elements/hardwareSelectorV2/types'
import useTotalStockAcrossSuppliers from 'elements/hardwareSelectorV2/useTotalStockAcrossSuppliers'
import IndicativePriceLabel from 'elements/IndicativePriceLabel'
import PriceOnApplicationPricingLabel from 'elements/PriceOnApplicationPricingLabel'
import SupplierLogoWithFallback from 'elements/SupplierLogoWithFallback'
import TieredPriceOnApplicationPricingLabel from 'elements/TieredPriceOnApplicationPricingLabel'
import {
  BaseTheme,
  Box,
  DeleteIcon,
  LocationIcon,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from 'opensolar-ui'
import { CART_MAX_QUANTITY, STOCK_LEVEL_STATUS } from 'pages/ordering/constants'
import useEnabledHardwareSuppliers from 'pages/ordering/hooks/useEnabledHardwareSuppliers'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import useIsIndicativePricing from 'pages/ordering/hooks/useIsIndicativePricing'
import useIsPriceOnApplication from 'pages/ordering/hooks/useIsPriceOnApplication'
import useIsTieredPriceOnApplication from 'pages/ordering/hooks/useIsTieredPriceOnApplication'
import SourceSelector from 'pages/ordering/orderByProjectCart/SourceSelector'
import type { ProjectInfo } from 'pages/ordering/orderByProjectCart/useOrderByProjectCartData'
import useProjectOrderPresenter from 'pages/ordering/ProjectOrderPresenter/useProjectOrderPresenter'
import HardwareSearchInputV3 from 'pages/ordering/search/HardwareSearchInputV3'
import { HardwareSupplierFilterKeyType } from 'pages/ordering/type'
import { getLineItemProductDisplayName, isManualSource, SELECTABLE_MANUAL_SOURCES } from 'pages/ordering/utils'
import { ProductImage } from 'projectSections/sections/hardware/bom/ProductImage'
import RemoveItemDialog from 'projectSections/sections/hardware/bom/RemoveItemDialog'
import { ProductNameCellSearchContainer, SearchInputContainer } from 'projectSections/sections/hardware/bom/styles'
import { useTranslate } from 'ra-core'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import useDistributorTradePricing from 'services/useDistributorTradePricing'
import { formatCurrencyWithSymbol, formatDecimalPlaces, isMountingComponent } from 'util/misc'
import DealBadge from '../deals/DealBadge'
import { useDealTotals } from '../deals/hooks/useDealTotals'
import { ItemDealResult } from '../deals/types'
import { CartTableHeaders } from './CartTableHeaders'
import type { ConsolidatedLineItem } from './useCartData'

const MANUAL_SOURCE_LABELS: Record<(typeof SELECTABLE_MANUAL_SOURCES)[number], string> = {
  exclude: 'Exclude from Order',
  '3rd_party': 'Purchased from 3rd Party',
  stock_on_hand: 'Stock on Hand',
}

/** Min height so Select Source cell is consistent with or without supplier logo */
const SELECT_SOURCE_MIN_HEIGHT = 36

const SelectSourceWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  width: '100%',
  overflow: 'hidden',
  '& .MuiSelect-root': {
    width: '100%',
    maxWidth: '100%',
    minHeight: SELECT_SOURCE_MIN_HEIGHT,
  },
  '& .MuiSelect-select': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: SELECT_SOURCE_MIN_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
  },
})

const StaticSourceDisplay = styled('div')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '4px',
  minHeight: SELECT_SOURCE_MIN_HEIGHT,
})

const StaticSourceLogo = styled('div')(({ theme }) => ({
  width: 34,
  height: 20,
  border: `1px solid ${theme.palette.neutralGrey[500]}`,
  borderRadius: 4,
  padding: '4px',
  flexShrink: 0,
}))

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
}))

const StyledTable = styled(Table)(({ theme }) => ({
  '& tbody tr td:first-of-type': {
    borderLeft: `1px solid ${theme.palette.grey[200]}`,
  },
  '& tbody tr td:last-of-type': {
    borderRight: `1px solid ${theme.palette.grey[200]}`,
  },
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: theme.palette.neutralGrey[100] ?? theme.palette.grey[100],
}))

const BomCell = styled(TableCell)<{ borderBottomLeftRadius?: string; borderRightRadius?: string; maxWidth?: string }>(
  ({ theme, borderBottomLeftRadius, borderRightRadius }) => ({
    borderTop: `1px solid ${theme.palette.grey[200]}`,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    padding: '10px 12px',
    verticalAlign: 'middle',
    borderRadius: 8,
    borderRightRadius,
    fontSize: '14px',
    '& [class*="Typography"]': { fontSize: '14px' },
    width: 'auto',
    maxWidth: '200px',
  })
)

const ProductCell = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  width: '100%',
})

const ProductInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '86%',
  gap: 1,
})

const ProductDescription = styled('span')({
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
  display: 'block',
})

const ProductCode = styled('span')(() => ({
  fontSize: '12px',
  color: '#6B6A76',
  fontStyle: 'inter',
}))

const PricePerUnit = styled('span')(() => ({
  fontSize: '12px',
  color: '#6B6A76',
  fontStyle: 'inter',
}))

const QuantityField = styled(TextField)({
  maxWidth: 56, // Match BOM page Qty column (BomTableRow QuantityField)
  '& .OSUI-InputBase-root': {
    minHeight: '36px !important',
    padding: '0 4px !important',
  },
  '& input': {
    padding: '2px 8px !important',
    textAlign: 'center',
  },
})

const StockStatusContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 4,
  width: 'auto',
})

const StyledLocationPin = styled(LocationIcon)({
  color: BaseTheme.palette.blue[900],
  width: 20,
  height: 20,
})

const ErrorCaption = styled('span')(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.red[600],
  fontWeight: 500,
}))
const PriceCellContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'flex-end',
  gap: 4,
})

const DeleteButton = styled('button')(({ theme }) => ({
  padding: '8px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: theme.palette.grey[600],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '4px',
  '&:hover': {
    color: theme.palette.red[600],
    backgroundColor: theme.palette.red[50],
  },
}))

const EmptyState = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 200,
  padding: 40,
  color: theme.palette.grey[600],
}))

const StockStatusWrapper = styled('div', { name: 'StockStatusWrapper' })({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 12,
  width: '100%',
})

type StaticSourceContentProps = {
  source: HardwareSupplierFilterKeyType | 'component' | undefined
  getSupplierConfig: ReturnType<typeof useGetSupplierConfig>['getSupplierConfig']
}

const StaticSourceContent = memo(({ source, getSupplierConfig }: StaticSourceContentProps) => {
  const translate = useTranslate()
  if (!source || source === 'component') {
    return (
      <StaticSourceDisplay>
        <Typography style={{ fontSize: 14, fontWeight: 500 }}>{translate('Select Your Source')}</Typography>
      </StaticSourceDisplay>
    )
  }

  const manualLabel = (SELECTABLE_MANUAL_SOURCES as readonly string[]).includes(source)
    ? MANUAL_SOURCE_LABELS[source as (typeof SELECTABLE_MANUAL_SOURCES)[number]]
    : null
  if (manualLabel) {
    return (
      <StaticSourceDisplay>
        <Typography style={{ fontSize: 14, fontWeight: 500 }}>{manualLabel}</Typography>
      </StaticSourceDisplay>
    )
  }

  const supplierConfig = getSupplierConfig(source)
  if (supplierConfig) {
    return (
      <StaticSourceDisplay>
        <StaticSourceLogo>
          <SupplierLogoWithFallback logoUrl={supplierConfig.logoUrl} name={supplierConfig.name} />
        </StaticSourceLogo>
        <Typography style={{ fontSize: 14, fontWeight: 500 }}>{supplierConfig.name}</Typography>
      </StaticSourceDisplay>
    )
  }

  return (
    <StaticSourceDisplay>
      <Typography style={{ fontSize: 14, fontWeight: 500 }}>{source}</Typography>
    </StaticSourceDisplay>
  )
})

const StyledTypographyWithLineThrough = styled(Typography)(({ theme }) => ({
  textDecoration: 'line-through',
  color: theme.palette.grey[600],
  fontSize: '10px !important',
}))

const TotalPrice = styled(Typography)(({ theme }) => ({
  fontSize: '12px !important',
  color: theme.palette.grey[1000],
}))

const UnitPrice = styled(Typography)(({ theme }) => ({
  fontSize: '10px !important',
  color: theme.palette.grey[600],
}))

type CartTablePriceProps = {
  totalPrice: number | null
  dealResult: ItemDealResult | null
  currencySymbol: string
  unitPrice: number | undefined
  isIndicativePricing: boolean
  isPriceOnApplication: boolean
  isTieredPriceOnApplication: boolean
  supplierFilterKey: HardwareSupplierFilterKeyType | undefined
}

const CartTablePrice: React.FC<CartTablePriceProps> = ({
  totalPrice,
  dealResult,
  currencySymbol,
  unitPrice,
  isIndicativePricing,
  isPriceOnApplication,
  isTieredPriceOnApplication,
  supplierFilterKey,
}) => {
  const translate = useTranslate()
  if (!totalPrice) return <UnitPrice variant="body4">{currencySymbol} -</UnitPrice>
  if (isTieredPriceOnApplication) {
    return <TieredPriceOnApplicationPricingLabel supplierFilterKey={supplierFilterKey} variant="small" />
  }
  if (isPriceOnApplication) {
    return <PriceOnApplicationPricingLabel variant="small" />
  }
  if (dealResult) {
    const isFree = dealResult.adjustedTotal === 0
    return (
      <>
        <TotalPrice colorHex={BaseTheme.palette.red[800]} style={{ fontWeight: 500 }}>
          {isFree
            ? translate('Free')
            : formatCurrencyWithSymbol(dealResult.adjustedTotal, currencySymbol, undefined, 2)}
        </TotalPrice>
        <StyledTypographyWithLineThrough>
          {formatCurrencyWithSymbol(totalPrice, currencySymbol, undefined, 2)}
        </StyledTypographyWithLineThrough>

        {!isFree && (
          <UnitPrice>
            {formatCurrencyWithSymbol(dealResult.adjustedUnitPrice, currencySymbol, undefined, 2)}/unit
          </UnitPrice>
        )}
      </>
    )
  }

  return (
    <>
      <TotalPrice>{`${isIndicativePricing ? '~' : ''}${formatCurrencyWithSymbol(
        totalPrice,
        currencySymbol,
        undefined,
        2
      )}`}</TotalPrice>
      <UnitPrice>{formatCurrencyWithSymbol(unitPrice, currencySymbol, undefined, 2)}/unit</UnitPrice>
      {isIndicativePricing && <IndicativePriceLabel />}
    </>
  )
}

type CartTableRowProps = {
  lineItem: ConsolidatedLineItem
  isProjectView: boolean
  projectInfoMap: Record<number, ProjectInfo>
  onQuantityChange: (lineItem: ConsolidatedLineItem, quantity: number) => void
  onSourceChange: (code: string, source: HardwareSupplierFilterKeyType | undefined) => void
  onDeleteClick: (lineItem: ConsolidatedLineItem) => void
  onChangesMade?: () => void
  itemDiscounts: Map<string, ItemDealResult>
}

const CartTableRow = memo(
  ({
    lineItem,
    projectInfoMap,
    onQuantityChange,
    onSourceChange,
    onDeleteClick,
    onChangesMade,
    isProjectView,
    itemDiscounts,
  }: CartTableRowProps) => {
    const theme = useTheme()
    const currencySymbol = useSelector(orgSelectors.getCurrencySymbolFromOrg)
    const countryIso2 = useSelector(orgSelectors.getOrgIso2)
    const { getSupplierConfig } = useGetSupplierConfig()
    const [quantity, setQuantity] = useState(lineItem.quantity)
    const [quantityError, setQuantityError] = useState<string | null>(null)
    const translate = useTranslate()

    const orderingData = lineItem.orderingData
    const hasMixedSources = lineItem.hasMixedSources || false
    const resolvedSource = lineItem.resolvedSource

    const availableDistributors = useMemo(() => {
      if (!orderingData) return []
      return orderingData
        .filter((d) => d.is_available)
        .map((d) => d.distributor)
        .filter((d): d is HardwareSupplierFilterKeyType => !!d)
    }, [orderingData])

    const [selectedSource, setSelectedSource] = useState<HardwareSupplierFilterKeyType | 'component' | undefined>(
      resolvedSource ?? lineItem.selectedDistributor
    )

    // Sync selectedSource when resolvedSource changes (from parent). Don't overwrite when user chose a manual source.
    // Only sync when effectiveResolvedSource actually changed (avoids reverting user selection before parent re-renders).
    const effectiveResolvedSource = resolvedSource ?? lineItem.selectedDistributor
    const prevEffectiveResolvedSourceRef = useRef(effectiveResolvedSource)
    useEffect(() => {
      if (selectedSource && isManualSource(selectedSource)) return
      if (prevEffectiveResolvedSourceRef.current !== effectiveResolvedSource) {
        prevEffectiveResolvedSourceRef.current = effectiveResolvedSource
        setSelectedSource(effectiveResolvedSource)
        if (lineItem.selectedDistributor !== effectiveResolvedSource) {
          onSourceChange(lineItem.code, effectiveResolvedSource)
        }
      }
    }, [effectiveResolvedSource, selectedSource, lineItem.selectedDistributor, lineItem.code, onSourceChange])

    // Sync quantity when lineItem changes
    useEffect(() => {
      setQuantity(lineItem.quantity)
      setQuantityError(null)
    }, [lineItem.quantity])

    // Get display info based on selected source
    const displayData = useMemo(() => {
      // For mixed sources or manual costs, don't show ordering data
      if (hasMixedSources || isManualSource(selectedSource)) {
        return null
      }
      return orderingData?.find((d: DistributorDataTypeV2) => d.distributor === selectedSource) || orderingData?.[0]
    }, [orderingData, selectedSource, hasMixedSources])

    const imageUrl = displayData?.image_url
    const productTitle = getLineItemProductDisplayName(
      hasMixedSources || isManualSource(selectedSource) ? undefined : (displayData ?? undefined),
      lineItem.componentTitle
    )
    const unitPrice = displayData?.price ?? displayData?.price_detail?.price_ex_vat

    const handleQuantityKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
        e.preventDefault()
      }
    }

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value
      if (raw === '') {
        setQuantity(0)
        setQuantityError(translate('1 or more'))
        return
      }
      const parsed = Math.floor(Number(raw))
      if (Number.isNaN(parsed)) {
        setQuantity(lineItem.quantity)
        setQuantityError(translate('1 or more'))
        return
      }
      if (parsed < 1) {
        setQuantity(parsed)
        setQuantityError(translate('1 or more'))
        return
      }
      if (parsed > CART_MAX_QUANTITY) {
        setQuantity(parsed)
        setQuantityError(translate('10,000 or less'))
        return
      }
      setQuantityError(null)
      setQuantity(parsed)
      onQuantityChange(lineItem, parsed)
      onChangesMade?.()
    }

    const handleQuantityBlur = () => {
      const normalised = Math.floor(Number(quantity))
      if (Number.isNaN(normalised) || normalised < 1) {
        setQuantity(lineItem.quantity)
        setQuantityError(translate('1 or more'))
        return
      }
      if (normalised > CART_MAX_QUANTITY) {
        setQuantity(CART_MAX_QUANTITY)
        setQuantityError(translate('10,000 or less'))
        onQuantityChange(lineItem, CART_MAX_QUANTITY)
        onChangesMade?.()
        return
      }
      setQuantityError(null)
      if (normalised !== quantity) {
        setQuantity(normalised)
        onQuantityChange(lineItem, normalised)
        onChangesMade?.()
      }
    }

    const handleSourceChange = (manufacturer: string) => {
      const source = manufacturer === 'default' ? undefined : (manufacturer as HardwareSupplierFilterKeyType)
      setSelectedSource(source)
      onSourceChange(lineItem.code, source)
      onChangesMade?.()
    }

    const handleDeleteClick = () => {
      onDeleteClick(lineItem)
      onChangesMade?.()
    }

    const getComponentCategory = () => {
      const categoryMap: Record<string, string> = {
        module: translate('Panels'),
        inverter: translate('Inverter'),
        battery: translate('Battery'),
        mounting: translate('Mounting'),
        other: translate('Other'),
      }
      const effectiveTypeV2 = lineItem.componentTypeV2 ?? lineItem.componentType ?? ''
      const componentCategory = isMountingComponent(effectiveTypeV2) ? 'mounting' : effectiveTypeV2
      return categoryMap[componentCategory] ?? translate('Other')
    }

    // Get stock availability across all suppliers (only for supplier sources)
    const { availableQuantity, isGlobalOutOfStock, showIsInStockElsewhere } = useTotalStockAcrossSuppliers(
      lineItem.orderingData ?? [],
      selectedSource as HardwareSupplierFilterKeyType
    )

    // Get stock status for the selected distributor
    const stockStatus = useMemo(() => {
      // For mixed sources or manual costs, return undefined
      if (hasMixedSources || isManualSource(selectedSource)) {
        return undefined
      }
      return getStockStatusMessagesPerDistributor({
        distributors: lineItem.orderingData,
        quantityRequired: quantity,
        availableQuantity,
      }).find(({ distributor }) => distributor === selectedSource)
    }, [lineItem?.orderingData, quantity, selectedSource, availableQuantity, hasMixedSources])

    // Get stock status color
    const stockStatusColor = useMemo(() => {
      // N/A: mixed sources, manual, or no stock message → black
      if (!stockStatus || !stockStatus?.productTileStockMessage?.title) {
        return theme.palette.grey[1000]
      }
      if (selectedSource === 'outlet') {
        if (
          [STOCK_LEVEL_STATUS.AVAILABLE, STOCK_LEVEL_STATUS.AVAILABLE_FOR_PRE_ORDER].includes(stockStatus?.status || '')
        ) {
          return theme.palette.green[600]
        }
        return theme.palette.red[800]
      }
      if (
        [
          STOCK_LEVEL_SCENARIO.PARTIALLY_IN_STOCK,
          STOCK_LEVEL_SCENARIO.OUT_OF_STOCK_WITH_INCOMING_SCHEDULE,
          STOCK_LEVEL_SCENARIO.OUT_OF_STOCK,
        ].includes(stockStatus?.scenario || '')
      ) {
        return theme.palette.red[800]
      } else if (stockStatus?.scenario === STOCK_LEVEL_SCENARIO.STOCK_AVAILABILITY_UPON_REQUEST) {
        return theme.palette.blue[900]
      }

      return theme.palette.green[600]
    }, [stockStatus, selectedSource, theme])

    // Calculate total price and unit price display
    const totalPrice = unitPrice && !isManualSource(selectedSource) && !hasMixedSources ? unitPrice * quantity : null
    const selectedSupplier =
      selectedSource && selectedSource !== 'component' ? (selectedSource as HardwareSupplierFilterKeyType) : undefined
    const isIndicativePricing = useIsIndicativePricing(selectedSupplier)
    const isPriceOnApplication = useIsPriceOnApplication(selectedSupplier)
    const isTieredPriceOnApplication = useIsTieredPriceOnApplication(selectedSupplier, unitPrice)

    // Prefer cart-level itemDiscounts so BXGY (incl. cross-product free items) is reflected
    const dealResult = useMemo(() => {
      if (!unitPrice || hasMixedSources || !selectedSource || isManualSource(selectedSource)) return null
      const result = itemDiscounts.get(`${lineItem.code}:${selectedSource}`)
      if (!result || result.totalDiscount <= 0) return null
      return result
    }, [unitPrice, hasMixedSources, selectedSource, itemDiscounts, lineItem.code])

    // Same Source: Price per watt (panels) or price per kWh (batteries) from selected source. Always per ONE unit.
    // Inverters: no subtext per GS-937.
    const pricePerRatingUnit = useMemo(() => {
      if (!unitPrice || !lineItem.rating || hasMixedSources || isManualSource(selectedSource)) {
        return null
      }

      if (lineItem.componentType === 'module') {
        // Panel rating is in kW; convert to watts for formula
        const ratingWatts = lineItem.rating * 1000
        if (countryIso2 === 'AU' || countryIso2 === 'US') {
          const pricePerWatt = unitPrice / ratingWatts
          return `Price per watt: ${formatCurrencyWithSymbol(pricePerWatt, currencySymbol, undefined, 2)}`
        }
        // UK: (Price per unit ÷ Rating in watts) × 100 = pence per watt
        const pencePerWatt = (unitPrice / ratingWatts) * 100
        return `Price per watt: ${formatDecimalPlaces(pencePerWatt, 2)}p`
      } else if (lineItem.componentType === 'battery') {
        const pricePerKwh = unitPrice / lineItem.rating
        return `Price per kWh: ${formatCurrencyWithSymbol(pricePerKwh, currencySymbol, undefined, 2)}`
      }
      return null
    }, [
      unitPrice,
      lineItem.rating,
      lineItem.componentType,
      hasMixedSources,
      selectedSource,
      currencySymbol,
      countryIso2,
    ])

    return (
      <>
        <BomCell borderBottomLeftRadius="8px">
          <ProductCell>
            <ProductImage image={imageUrl} />
            <ProductInfo>
              {!hasMixedSources && productTitle && <ProductDescription>{productTitle}</ProductDescription>}
              <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '16px' }}>
                <ProductCode>
                  {lineItem.code}
                  {!hasMixedSources && displayData?.code && lineItem.code !== displayData.code && (
                    <> (SKU: {displayData.code})</>
                  )}
                </ProductCode>
                {pricePerRatingUnit && <PricePerUnit>{pricePerRatingUnit}</PricePerUnit>}
              </div>
              <DealBadge
                orderingV2={lineItem.orderingData ?? []}
                selectedDistributor={lineItem.selectedDistributor}
                currentProductCode={lineItem.code}
                variant="light"
              />
            </ProductInfo>
          </ProductCell>
        </BomCell>
        <BomCell>{getComponentCategory()}</BomCell>
        <BomCell>
          {isProjectView ? (
            <Typography variant="body1">{quantity}</Typography>
          ) : (
            <Box display="flex" flexDirection="column" style={{ gap: 4 }}>
              <QuantityField
                value={quantity}
                onChange={handleQuantityChange}
                onBlur={handleQuantityBlur}
                onKeyDown={handleQuantityKeyDown}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              />
              {quantityError && (
                <Typography textVariant="helper1" colorHex={(theme.palette.error?.main ?? '#F44336') as string}>
                  <span style={{ fontSize: '10px' }}>{quantityError}</span>
                </Typography>
              )}
            </Box>
          )}
        </BomCell>
        <BomCell>
          {isProjectView ? (
            <StaticSourceContent
              source={hasMixedSources ? undefined : selectedSource}
              getSupplierConfig={getSupplierConfig}
            />
          ) : (
            <SelectSourceWrapper>
              <SourceSelector
                value={hasMixedSources ? undefined : selectedSource}
                onChange={(source) => handleSourceChange(source ?? 'default')}
                availableDistributors={availableDistributors}
              />
              {hasMixedSources && <ErrorCaption>{translate('This item has multiple sources')}</ErrorCaption>}
            </SelectSourceWrapper>
          )}
        </BomCell>
        <BomCell>
          <StockStatusWrapper>
            <StockStatusContainer color={stockStatusColor} gridGap={8}>
              {hasMixedSources || isManualSource(selectedSource) ? (
                <Typography variant="body1">N/A</Typography>
              ) : stockStatus?.productTileStockMessage?.title ? (
                <Typography variant="body1">
                  <span style={{ whiteSpace: 'nowrap' }}>{stockStatus?.productTileStockMessage?.title}</span>
                </Typography>
              ) : (
                <Typography variant="body1">N/A</Typography>
              )}
            </StockStatusContainer>
            {!hasMixedSources && !isManualSource(selectedSource) && showIsInStockElsewhere && (
              <StockStatusContainer color={theme.palette.blue[900]} gridGap={8}>
                <Typography variant="body3">{translate('Available in other locations')}</Typography>
              </StockStatusContainer>
            )}
            {!hasMixedSources &&
              !isManualSource(selectedSource) &&
              !isGlobalOutOfStock &&
              (!!stockStatus?.productTileStockMessage?.title || availableQuantity === 0) && (
                <AvailableStocksButton
                  icon={<StyledLocationPin />}
                  data={lineItem.orderingData ?? []}
                  selectedDistributor={selectedSource as HardwareSupplierFilterKeyType}
                  productCode={lineItem.code}
                  productName={productTitle}
                  amplitudeEventType="Hardware Product"
                />
              )}
          </StockStatusWrapper>
        </BomCell>
        <BomCell style={{ textAlign: 'right' }}>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <CartTablePrice
              totalPrice={totalPrice}
              dealResult={dealResult}
              currencySymbol={currencySymbol}
              unitPrice={unitPrice}
              isIndicativePricing={isIndicativePricing}
              isPriceOnApplication={isPriceOnApplication}
              isTieredPriceOnApplication={isTieredPriceOnApplication}
              supplierFilterKey={selectedSupplier}
            />
          </Box>
        </BomCell>
        {/* Temporarily hidden: Project Allocation column
        {!isProjectView && (
          <BomCell>
            <ProjectAllocationContainer>
              {projectAllocations.length > 0 ? (
                isSingleProject ? (
                  <ProjectAllocationSingle>{projectAllocations[0].firstLine}</ProjectAllocationSingle>
                ) : (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'disc' }}>
                    {projectAllocations.map((allocation, idx) => (
                      <ProjectAllocationBullet key={idx}>{allocation.firstLine}</ProjectAllocationBullet>
                    ))}
                  </ul>
                )
              ) : (
                <Typography variant="caption" color="textSecondary">
                  No allocation
                </Typography>
              )}
            </ProjectAllocationContainer>
          </BomCell>
        )}
        */}

        {!isProjectView && (
          <BomCell align="center">
            <DeleteButton onClick={handleDeleteClick}>
              <DeleteIcon width="20" height="20" />
            </DeleteButton>
          </BomCell>
        )}
      </>
    )
  }
)

export type AddItemRowProps = {
  showSuggestion: boolean
  setShowSuggestion: (v: boolean) => void
  onSelectComponent: (item: unknown) => void
  onBrowse: () => void
}

type CartTableProps = {
  lineItems: ConsolidatedLineItem[]
  isLoading: boolean
  projectInfoMap: Record<number, ProjectInfo>
  isSingleProject: boolean
  onChangesMade?: () => void
  isProjectView?: boolean
  onClearCartClick?: () => void
  /** When set, an "Add item" search row is rendered as the last row in the table */
  addItemRow?: AddItemRowProps
}

const CartTable = ({
  lineItems,
  isLoading,
  projectInfoMap,
  isSingleProject,
  onChangesMade,
  isProjectView = true,
  onClearCartClick,
  addItemRow,
}: CartTableProps) => {
  const projectOrderPresenter = useProjectOrderPresenter()
  const [removeModalLineItem, setRemoveModalLineItem] = useState<ConsolidatedLineItem | null>(null)
  const { filteredSuppliers: enabledDistributors } = useEnabledHardwareSuppliers()
  const { distributorTradePricing, isChecking } = useDistributorTradePricing({
    orgId: useSelector(orgSelectors.getOrg)?.id || 0,
    distributors: enabledDistributors,
  })
  const translate = useTranslate()
  const handleQuantityChange = useCallback(
    (lineItem: ConsolidatedLineItem, quantity: number) => {
      // Always use setTotalQuantityByCode so the consolidated total updates correctly
      // for both increases and decreases, whether single or multi project
      projectOrderPresenter.setTotalQuantityByCode(lineItem.code, quantity)
    },
    [projectOrderPresenter]
  )

  const handleSourceChange = useCallback(
    (code: string, source: HardwareSupplierFilterKeyType | undefined) => {
      projectOrderPresenter.updateLineItemDistributorByCode({ code, distributor: source })
    },
    [projectOrderPresenter]
  )

  const handleDeleteClick = useCallback((lineItem: ConsolidatedLineItem) => {
    setRemoveModalLineItem(lineItem)
  }, [])

  const handleRemoveModalClose = useCallback(() => {
    setRemoveModalLineItem(null)
  }, [])

  const handleRemoveItem = useCallback(() => {
    if (!removeModalLineItem) return
    projectOrderPresenter.deleteLineItemsByCode(removeModalLineItem.code)
    onChangesMade?.()
    setRemoveModalLineItem(null)
  }, [projectOrderPresenter, removeModalLineItem, onChangesMade])

  // Sort by category: Panels first, then Mounting, Inverter, Battery, Other (asc like design tab / project shop BOM)
  const categorySortOrder: Record<string, number> = {
    module: 1,
    mounting: 2,
    inverter: 3,
    battery: 4,
    other: 5,
  }
  const sortedLineItems = useMemo(() => {
    const getSortKey = (item: ConsolidatedLineItem) => {
      const effectiveTypeV2 = item.componentTypeV2 ?? item.componentType ?? 'other'
      const category = isMountingComponent(effectiveTypeV2) ? 'mounting' : (item.componentType ?? 'other')
      return categorySortOrder[category] ?? 5
    }
    return [...lineItems].sort((a, b) => getSortKey(a) - getSortKey(b))
  }, [lineItems])

  const { itemDiscounts } = useDealTotals(lineItems)

  if (lineItems.length === 0 && !isLoading) {
    return (
      <EmptyState>
        <Typography>{translate('No items in cart')}</Typography>
      </EmptyState>
    )
  }

  return (
    <DistributorTradePricingContext.Provider value={{ distributorTradePricing, isChecking }}>
      <StyledTable>
        <StyledTableHead>
          <CartTableHeaders isProjectView={isProjectView} isLoading={isLoading} onClearCartClick={onClearCartClick} />
        </StyledTableHead>
        <TableBody>
          {sortedLineItems.map((lineItem) => (
            <StyledTableRow key={lineItem.uuid}>
              <CartTableRow
                lineItem={lineItem}
                projectInfoMap={projectInfoMap}
                onQuantityChange={handleQuantityChange}
                onSourceChange={handleSourceChange}
                onDeleteClick={handleDeleteClick}
                onChangesMade={onChangesMade}
                isProjectView={isProjectView}
                itemDiscounts={itemDiscounts}
              />
            </StyledTableRow>
          ))}
          {addItemRow && (
            <StyledTableRow>
              <BomCell borderBottomLeftRadius="8px">
                <ProductNameCellSearchContainer>
                  <ProductImage />
                  <SearchInputContainer>
                    <HardwareSearchInputV3
                      onEnter={() => null}
                      onSelect={addItemRow.onSelectComponent}
                      isShop3={true}
                      variant="outlined"
                      location="bom"
                      showSuggestion={addItemRow.showSuggestion}
                      setShowSuggestion={addItemRow.setShowSuggestion}
                      onBrowse={addItemRow.onBrowse}
                    />
                  </SearchInputContainer>
                </ProductNameCellSearchContainer>
              </BomCell>
              {isProjectView ? (
                <>
                  <BomCell />
                  <BomCell />
                  <BomCell />
                  <BomCell />
                  <BomCell borderRightRadius="8px" />
                </>
              ) : (
                <>
                  <BomCell />
                  <BomCell />
                  <BomCell />
                  <BomCell />
                  <BomCell />
                  <BomCell borderRightRadius="8px" />
                </>
              )}
            </StyledTableRow>
          )}
        </TableBody>
      </StyledTable>

      {removeModalLineItem && !isProjectView && (
        <RemoveItemDialog
          componentTitle={removeModalLineItem.code}
          handleClose={handleRemoveModalClose}
          handleRemoveItem={handleRemoveItem}
        />
      )}
    </DistributorTradePricingContext.Provider>
  )
}

export default memo(CartTable)
